import React, { useState, useMemo } from "react";
import useSWR, { mutate } from "swr";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR, formatDate, todayISO } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const CATEGORIES = [
  { v: "renovasi", label: "Renovasi" },
  { v: "pajak", label: "Pajak" },
  { v: "komisi", label: "Komisi" },
  { v: "utilitas", label: "Utilitas" },
  { v: "insidental", label: "Insidental" },
  { v: "lainnya", label: "Lainnya" },
];

const CAT_TONE = {
  renovasi: "bg-info/15 text-info",
  pajak: "bg-warning/15 text-warning",
  komisi: "bg-primary/10 text-primary",
  utilitas: "bg-secondary text-secondary-foreground",
  insidental: "bg-destructive/10 text-destructive",
  lainnya: "bg-secondary text-secondary-foreground",
};

const empty = { date: todayISO(), category: "insidental", amount: "", description: "", unit_id: "", receipt_no: "", notes: "" };

export default function Expenses() {
  const [filters, setFilters] = useState({ category: "all", unit_id: "all" });
  const qs = new URLSearchParams();
  if (filters.category !== "all") qs.set("category", filters.category);
  if (filters.unit_id !== "all") qs.set("unit_id", filters.unit_id);

  const { data: expenses = [], isLoading } = useSWR(`/expenses?${qs.toString()}`, fetcher);
  const { data: units = [] } = useSWR("/units", fetcher);
  const unitMap = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const total = expenses.reduce((s, e) => s + Number(e.amount), 0);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...e, amount: e.amount, unit_id: e.unit_id || "" }); setOpen(true); };

  const submit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        amount: Number(form.amount),
        unit_id: form.unit_id || null,
      };
      if (editing) {
        await api.put(`/expenses/${editing.id}`, body);
        toast.success("Biaya diperbarui");
      } else {
        await api.post("/expenses", body);
        toast.success("Biaya dicatat");
      }
      setOpen(false);
      mutate((k) => typeof k === "string" && k.startsWith("/expenses"));
      mutate("/dashboard/stats");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (e) => {
    if (!confirm("Hapus biaya ini?")) return;
    try {
      await api.delete(`/expenses/${e.id}`);
      toast.success("Biaya dihapus");
      mutate((k) => typeof k === "string" && k.startsWith("/expenses"));
      mutate("/dashboard/stats");
    } catch (er) {
      toast.error(formatApiErrorDetail(er?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Operasional</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Biaya & Pengeluaran</h1>
          <p className="text-sm text-muted-foreground mt-1">Catat renovasi, pajak, komisi, utilitas, dan biaya insidental.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="add-expense-button">
          <Plus className="h-4 w-4" /> Catat Biaya
        </Button>
      </header>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Kategori</Label>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger data-testid="expense-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Unit</Label>
            <Select value={filters.unit_id} onValueChange={(v) => setFilters({ ...filters, unit_id: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua unit</SelectItem>
                {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.code} — {u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end justify-end">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Total (filter)</div>
              <div className="font-display text-2xl font-medium tabular-nums" data-testid="expense-total">{formatIDR(total)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : expenses.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
              <Wallet className="h-7 w-7" />
            </div>
            <div className="font-display text-lg font-medium">Belum ada catatan biaya</div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {expenses.map((e) => (
            <Card key={e.id} data-testid={`expense-row-${e.id}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${CAT_TONE[e.category]}`}>
                      {CATEGORIES.find((c) => c.v === e.category)?.label || e.category}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{formatDate(e.date)}</span>
                    {e.unit_id && unitMap[e.unit_id] && (
                      <Badge variant="outline" className="rounded-md font-mono">{unitMap[e.unit_id].code}</Badge>
                    )}
                  </div>
                  <div className="font-medium mt-1">{e.description || "—"}</div>
                  {e.notes && <div className="text-xs text-muted-foreground mt-0.5">{e.notes}</div>}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-display text-lg font-medium tabular-nums text-destructive">{formatIDR(e.amount)}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(e)} data-testid={`delete-expense-${e.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Biaya" : "Catat Biaya"}</DialogTitle>
            <DialogDescription>Pilih kategori dan unit terkait (opsional) untuk laporan pendapatan.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tanggal</Label>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Deskripsi</Label>
              <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nominal (Rp)</Label>
                <Input type="number" min="0" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Unit (opsional)</Label>
                <Select value={form.unit_id || "none"} onValueChange={(v) => setForm({ ...form, unit_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Tidak spesifik —</SelectItem>
                    {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.code} — {u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Nomor Bukti</Label>
              <Input value={form.receipt_no} onChange={(e) => setForm({ ...form, receipt_no: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Catatan</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="expense-save-button">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
